import forge from 'node-forge'

/** 与后端 {@link com.uav.common.utils.rsa.RSAUtils#PUBLIC_KEY} 一致 */
const DEFAULT_PUBLIC_KEY =
  'MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAseQ2yb9Q9LYnBAvRJF2sjFk8OM8PszsAXTcmPSewKHQUyCypBnm5MyzTSodsIypa2ZdHZ1/4RSDOAR9z1jqgD4fSR8yWFgDBIieHEUw8tz2i9HTlG+N2F+sya42l8rH30Rz8qmHr8osYUGbikHr5XCqBaCFQvXzHlQe93onZQNEyy15Y3Yc24cSRSf/KmMyJgRTkSGe9lHgT7jbFLhB7yZiJXoVCfMxp7FUp3x6QDTkHIdhnxft80mcCDWZ+rsZ17Rb1sQQ8BgeSw1mSapXSpPppbJ24qFGc47Pgnb0xeWzBxTGcd9+Kjaxpi3SRFSG0LAnWuHrZE7rqwteS19BR1wIDAQAB'

const MAX_ENCRYPT_BLOCK = 245
const MAX_DECRYPT_BLOCK = 256

let cachedPublicKey = null

function getPublicKeyB64 () {
  const fromEnv = import.meta.env.VITE_APP_API_CRYPTO_PUBLIC_KEY
  return (fromEnv && String(fromEnv).trim()) || DEFAULT_PUBLIC_KEY
}

function getPublicKey () {
  if (!cachedPublicKey) {
    const der = forge.util.decode64(getPublicKeyB64())
    cachedPublicKey = forge.pki.publicKeyFromAsn1(forge.asn1.fromDer(der))
  }
  return cachedPublicKey
}

export function isApiCryptoEnabled () {
  return import.meta.env.VITE_APP_API_CRYPTO_ENABLED === 'true'
}

function getEncryptDataField () {
  return import.meta.env.VITE_APP_API_CRYPTO_DATA_FIELD || 'encryptData'
}

function getCryptoHeaderName () {
  return import.meta.env.VITE_APP_API_CRYPTO_HEADER_NAME || 'X-Api-Encrypt'
}

function getCryptoHeaderValue () {
  return import.meta.env.VITE_APP_API_CRYPTO_HEADER_VALUE || '1'
}

function getExcludePatterns () {
  const raw = import.meta.env.VITE_APP_API_CRYPTO_EXCLUDE_PATHS
  const defaults = [
    '/swagger-ui',
    '/swagger-ui.html',
    '/doc.html',
    '/webjars',
    '/v3/api-docs',
    '/druid',
    '/common/download',
    '/common/download/resource',
    '/actuator'
  ]
  if (!raw || !String(raw).trim()) {
    return defaults
  }
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

/** Path 密文 URL-safe：+ -> -，/ -> _，去掉 = */
export function toUrlSafePathSegment (base64Cipher) {
  if (!base64Cipher) {
    return base64Cipher
  }
  return base64Cipher.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

export function fromUrlSafePathSegment (urlSafeCipher) {
  if (!urlSafeCipher) {
    return urlSafeCipher
  }
  if (urlSafeCipher.includes('+') || urlSafeCipher.includes('/')) {
    return urlSafeCipher
  }
  let base64 = urlSafeCipher.replace(/-/g, '+').replace(/_/g, '/')
  const mod = base64.length % 4
  if (mod > 0) {
    base64 += '===='.substring(mod)
  }
  return base64
}

function matchAntPath (path, pattern) {
  const normalized = path.split('?')[0]
  const regex = pattern
    .replace(/\*\*/g, '\0')
    .replace(/\*/g, '[^/]*')
    .replace(/\?/g, '.')
    .replace(/\0/g, '.*')
  return new RegExp(`^${regex}$`).test(normalized)
}

export function isExcludedPath (url) {
  if (!url) {
    return false
  }
  const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0]
  return getExcludePatterns().some((pattern) => matchAntPath(path, pattern))
}

/** RSA 公钥加密（与后端 publicKeyEncrypt 分块一致） */
export function publicKeyEncrypt (plainText) {
  const publicKey = getPublicKey()
  const bytes = new TextEncoder().encode(plainText)
  const buf = forge.util.createBuffer()
  for (let offset = 0; offset < bytes.length; offset += MAX_ENCRYPT_BLOCK) {
    const chunk = forge.util.binary.raw.encode(bytes.subarray(offset, offset + MAX_ENCRYPT_BLOCK))
    buf.putBytes(publicKey.encrypt(chunk, 'RSAES-PKCS1-V1_5'))
  }
  return forge.util.encode64(buf.getBytes())
}

/** RSA 公钥解密（后端私钥 encryptByPrivateKey 的响应） */
export function publicKeyDecrypt (cipherText) {
  const publicKey = getPublicKey()
  const normalized = fromUrlSafePathSegment(cipherText)
  const input = forge.util.decode64(normalized)
  const buf = forge.util.createBuffer()
  for (let offset = 0; offset < input.length; offset += MAX_DECRYPT_BLOCK) {
    const chunk = input.substring(offset, offset + MAX_DECRYPT_BLOCK)
    buf.putBytes(forge.pki.rsa.decrypt(chunk, publicKey, true))
  }
  return buf.toString('utf8')
}

/** 加密 Path 变量（放入 URL 前请使用 URL-safe 形式；未开启 VITE_APP_API_CRYPTO_ENABLED 时原样返回） */
export function encryptPathSegment (plain) {
  if (plain === null || plain === undefined) {
    return plain
  }
  if (!isApiCryptoEnabled()) {
    return String(plain)
  }
  return toUrlSafePathSegment(publicKeyEncrypt(String(plain)))
}

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const UUID_PATTERN_1 = /^[0-9a-f]{15}[89ab][0-9a-f]{15}$/i

/** 判断 Path 段是否为需加密的动态 ID */
function shouldEncryptPathSegment (segment) {
  if (!segment) {
    return false
  }
  if (/^[A-Za-z0-9+/=_-]{64,}$/.test(segment)) {
    return false
  }
  if (/^\d+$/.test(segment) || /^[\d,]+$/.test(segment)) {
    return true
  }
  if (UUID_PATTERN.test(segment)) {
    return true
  }
  if (UUID_PATTERN_1.test(segment)) {
    return true
  }
  if (/^[0-9a-f]{24}$/i.test(segment)) {
    return true
  }
  return false
}

function encryptDynamicPathInUrl (url) {
  if (!url || url.startsWith('http')) {
    return url
  }
  const qIndex = url.indexOf('?')
  const path = qIndex >= 0 ? url.slice(0, qIndex) : url
  const query = qIndex >= 0 ? url.slice(qIndex) : ''
  const encryptedPath = path
    .split('/')
    .map((seg) => (shouldEncryptPathSegment(seg) ? encryptPathSegment(seg) : seg))
    .join('/')
  return encryptedPath + query
}

function extractCipherText (payload, field) {
  if (payload && typeof payload === 'object' && field in payload && payload[field] != null) {
    return String(payload[field])
  }
  if (typeof payload === 'string') {
    const trimmed = payload.trim()
    if (trimmed.startsWith('{')) {
      try {
        const parsed = JSON.parse(trimmed)
        if (parsed[field] != null) {
          return String(parsed[field])
        }
      } catch (e) {
        /* 非 JSON 密文 */
      }
    }
    return trimmed
  }
  return null
}

function formDataContainsFile (formData) {
  if (!formData || typeof formData.entries !== 'function') {
    return false
  }
  for (const [, value] of formData.entries()) {
    if ((typeof File !== 'undefined' && value instanceof File) ||
        (typeof Blob !== 'undefined' && value instanceof Blob)) {
      return true
    }
  }
  return false
}

function formDataToPlainObject (formData) {
  const obj = {}
  formData.forEach((value, key) => {
    const normalized = typeof value === 'string' ? value : String(value)
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      const existing = obj[key]
      obj[key] = Array.isArray(existing) ? [...existing, normalized] : [existing, normalized]
    } else {
      obj[key] = normalized
    }
  })
  return obj
}

function shouldApplyRequestCrypto (config) {
  if (!isApiCryptoEnabled()) {
    return false
  }
  if ((config.headers || {}).apiCrypto === false) {
    return false
  }
  if (config.method && config.method.toUpperCase() === 'OPTIONS') {
    return false
  }
  if (isExcludedPath(config.url)) {
    return false
  }
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    return !formDataContainsFile(config.data)
  }
  const contentType = String((config.headers || {})['Content-Type'] || (config.headers || {})['content-type'] || '')
  if (contentType.toLowerCase().includes('multipart/form-data')) {
    return false
  }
  return true
}

function encryptQueryParams (params) {
  if (!params || typeof params !== 'object') {
    return params
  }
  const encrypted = {}
  Object.keys(params).forEach((key) => {
    const val = params[key]
    if (val === undefined || val === null) {
      return
    }
    if (Array.isArray(val)) {
      encrypted[key] = val.map((item) => publicKeyEncrypt(String(item)))
    } else {
      encrypted[key] = publicKeyEncrypt(String(val))
    }
  })
  return encrypted
}

/**
 * 请求拦截：入参 RSA 公钥加密，与后端 api.crypto 对齐。
 * Path 变量请使用 {@link encryptPathSegment} 在拼接 URL 时手动加密。
 */
export function applyApiCryptoRequest (config) {
  if (!shouldApplyRequestCrypto(config)) {
    return config
  }

  const field = getEncryptDataField()
  const headerName = getCryptoHeaderName()
  const headerValue = getCryptoHeaderValue()
  config.headers = config.headers || {}
  config.headers[headerName] = headerValue

  if (config.url) {
    config.url = encryptDynamicPathInUrl(config.url)
  }

  const method = (config.method || 'get').toLowerCase()
  const contentType = String(config.headers['Content-Type'] || config.headers['content-type'] || '')

  if (config.params && Object.keys(config.params).length > 0) {
    config.params = encryptQueryParams(config.params)
  }

  if (method === 'get' || method === 'head' || method === 'delete') {
    return config
  }

  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const plain = JSON.stringify(formDataToPlainObject(config.data))
    config.data = { [field]: publicKeyEncrypt(plain) }
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
    return config
  }

  if (contentType.toLowerCase().includes('application/x-www-form-urlencoded')) {
    const plain =
      typeof config.data === 'string'
        ? config.data
        : JSON.stringify(config.data || {})
    config.data = { [field]: publicKeyEncrypt(plain) }
    return config
  }

  const plainBody =
    config.data === undefined || config.data === null
      ? ''
      : typeof config.data === 'string'
        ? config.data
        : JSON.stringify(config.data)

  if (plainBody) {
    config.data = { [field]: publicKeyEncrypt(plainBody) }
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
  }

  return config
}

/**
 * 请求拦截：入参 RSA 公钥加密，与后端 api.crypto 对齐。
 * Path 变量请使用 {@link encryptPathSegment} 在拼接 URL 时手动加密。
 */
export function applyApiCryptoRequestForGk (config) {
  const noEncryptPatterns = [
    /\/workspaces\/[^/]+\/waylines\/[^/]+\/url$/ // 匹配 downloadWaylineFile 的路径
  ]

  const shouldSkip = noEncryptPatterns.some(pattern => pattern.test(config.url))
  if (shouldSkip) {
    return config // 直接返回，不添加加密头、不加密 URL、参数、请求体
  }

  if (!shouldApplyRequestCrypto(config)) {
    return config
  }

  const field = 'encrypt_data'
  const headerName = getCryptoHeaderName()
  const headerValue = getCryptoHeaderValue()
  config.headers = config.headers || {}
  config.headers[headerName] = headerValue

  if (config.url) {
    config.url = encryptDynamicPathInUrl(config.url)
  }

  const method = (config.method || 'get').toLowerCase()
  const contentType = String(config.headers['Content-Type'] || config.headers['content-type'] || '')

  if (config.params && Object.keys(config.params).length > 0) {
    config.params = encryptQueryParams(config.params)
  }

  if (method === 'get' || method === 'head' || method === 'delete') {
    return config
  }

  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const plain = JSON.stringify(formDataToPlainObject(config.data))
    config.data = { [field]: publicKeyEncrypt(plain) }
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
    return config
  }

  if (contentType.toLowerCase().includes('application/x-www-form-urlencoded')) {
    const plain =
      typeof config.data === 'string'
        ? config.data
        : JSON.stringify(config.data || {})
    config.data = { [field]: publicKeyEncrypt(plain) }
    return config
  }

  const plainBody =
    config.data === undefined || config.data === null
      ? ''
      : typeof config.data === 'string'
        ? config.data
        : JSON.stringify(config.data)

  if (plainBody) {
    config.data = { [field]: publicKeyEncrypt(plainBody) }
    config.headers['Content-Type'] = 'application/json;charset=utf-8'
  }

  return config
}

function isEncryptedResponse (res, field) {
  const headerName = getCryptoHeaderName().toLowerCase()
  const headers = res.headers || {}
  const headerVal = headers[headerName] ?? headers[getCryptoHeaderName()]
  if (headerVal === getCryptoHeaderValue() || headerVal === '1') {
    return true
  }
  if (!field) {
    field = getEncryptDataField()
  }
  const data = res.data
  return data && typeof data === 'object' && field in data && data[field] != null
}

/**
 * 响应拦截：出参 RSA 公钥解密。
 */
export function applyApiCryptoResponse (res) {
  if (!isApiCryptoEnabled()) {
    return res
  }
  if (res.request?.responseType === 'blob' || res.request?.responseType === 'arraybuffer') {
    return res
  }
  if (!isEncryptedResponse(res, 'encryptData')) {
    return res
  }

  const cipher = extractCipherText(res.data, 'encryptData')
  if (!cipher) {
    return res
  }

  try {
    const plain = publicKeyDecrypt(cipher)
    res.data = plain ? JSON.parse(plain) : res.data
  } catch (e) {
    console.error('[api-crypto] 响应解密失败', e)
    return Promise.reject(new Error('响应解密失败'))
  }
  return res
}

/**
 * 响应拦截：出参 RSA 公钥解密。
 */
export function applyApiCryptoResponseForGk (res) {
  if (!isApiCryptoEnabled()) {
    return res
  }
  if (res.request?.responseType === 'blob' || res.request?.responseType === 'arraybuffer') {
    return res
  }
  if (!isEncryptedResponse(res, 'encrypt_data')) {
    return res
  }

  const cipher = extractCipherText(res.data, 'encrypt_data')
  if (!cipher) {
    return res
  }

  try {
    const plain = publicKeyDecrypt(cipher)
    res.data = plain ? JSON.parse(plain) : res.data
  } catch (e) {
    console.error('[api-crypto] 响应解密失败', e)
    return Promise.reject(new Error('响应解密失败'))
  }
  return res
}
